import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, CircularProgress, IconButton, Stack, Tab, Tabs, TextField, Typography, useTheme } from '@mui/material';
import { IconPlus, IconBox, IconBriefcase, IconPhotoOff, IconSearch, IconTrash, IconX } from '@tabler/icons';
import { useHistory } from 'react-router-dom';
import api from '../../services/api';
import { mediaUrl, precoResumo } from './itemUtils';

const PAGE_SIZE = 60;

// Hub único de Produtos/Serviços - grid de cards quadrados com foto de
// capa, separados por Tabs, com um botão "Adicionar" que leva pra uma
// tela própria (`ItemFormPage.js`, sem Drawer). Visual próprio (gradientes/
// glass), não reaproveita o estilo padrão do resto do sistema - só o
// suporte a tema dark/light.
const ItemsHubPage = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const history = useHistory();

    const [appId, setAppId] = useState(null);
    const [tab, setTab] = useState('produto');
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const loadedCountRef = useRef(PAGE_SIZE);

    useEffect(() => {
        api.get('v1/apps/default/')
            .then((response) => setAppId(response.data.app.id))
            .catch((error) => console.error(error));
    }, []);

    // Contas com centenas de itens não podem mais carregar tudo numa
    // passada só - "Carregar mais" refaz a busca do zero com um limit
    // maior (mesmo padrão já usado em `CampaignDetailDialog.js`), não
    // soma offset - assim uma busca nova sempre reflete o total certo.
    useEffect(() => {
        const timer = setTimeout(() => setSearch(searchInput.trim()), 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const loadItems = async (limit) => {
        if (!appId) return;
        try {
            const params = new URLSearchParams({ tipo: tab, limit: String(limit), offset: '0' });
            if (search) params.set('search', search);
            const response = await api.get(`v1/apps/${appId}/catalog_items/?${params.toString()}`);
            setItems(response.data?.results || []);
            setTotal(response.data?.total || 0);
            loadedCountRef.current = Math.max(limit, (response.data?.results || []).length);
        } catch (error) {
            console.error('Erro ao carregar itens', error);
        }
    };

    useEffect(() => {
        if (!appId) return;
        loadedCountRef.current = PAGE_SIZE;
        setLoading(true);
        loadItems(PAGE_SIZE).finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, tab, search]);

    const handleLoadMore = async () => {
        setLoadingMore(true);
        await loadItems(loadedCountRef.current + PAGE_SIZE);
        setLoadingMore(false);
    };

    const handleDelete = async (e, item) => {
        e.stopPropagation();
        if (!window.confirm(`Apagar "${item.nome}"? Essa ação não pode ser desfeita.`)) return;

        setDeletingId(item.id);
        try {
            await api.delete(`v1/apps/${appId}/catalog_items/${item.id}/`);
            setItems((prev) => prev.filter((i) => i.id !== item.id));
            setTotal((prev) => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Erro ao apagar item', error);
            alert(error.response?.data?.error || 'Não foi possível apagar. Tente novamente.');
        } finally {
            setDeletingId(null);
        }
    };

    const cardBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.9)';
    const cardBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)';
    const pageBg = isDark
        ? 'radial-gradient(circle at 15% 0%, rgba(124,58,237,0.16), transparent 45%), radial-gradient(circle at 85% 10%, rgba(37,99,235,0.16), transparent 45%)'
        : 'radial-gradient(circle at 15% 0%, rgba(124,58,237,0.08), transparent 45%), radial-gradient(circle at 85% 10%, rgba(37,99,235,0.08), transparent 45%)';

    return (
        <Box sx={{ minHeight: '100%', background: pageBg, p: { xs: 2, md: 4 } }}>
            {/* HERO */}
            <Box
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '28px',
                    p: { xs: 3, md: 4 },
                    mb: 3,
                    background: isDark
                        ? 'linear-gradient(135deg, rgba(37,99,235,0.35), rgba(124,58,237,0.35))'
                        : 'linear-gradient(135deg, #eef2ff, #f5f3ff)',
                    border: `1px solid ${cardBorder}`,
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2
                }}
            >
                <Box>
                    <Chip
                        icon={<IconBox size={14} />}
                        label="Catálogo"
                        size="small"
                        sx={{ mb: 1.5, fontWeight: 700, bgcolor: isDark ? 'rgba(255,255,255,0.12)' : '#fff' }}
                    />
                    <Typography
                        sx={{
                            fontWeight: 900,
                            fontSize: { xs: '1.8rem', md: '2.4rem' },
                            letterSpacing: '-1px',
                            background: 'linear-gradient(90deg,#2563eb,#7c3aed)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}
                    >
                        Produtos &amp; Serviços
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 520, mt: 0.5 }}>
                        Cadastre e gerencie tudo que você vende num lugar só.
                    </Typography>
                </Box>

                <Button
                    variant="contained"
                    size="large"
                    startIcon={<IconPlus size={20} />}
                    onClick={() => history.push('/dashboard/config/new_catalog')}
                    sx={{
                        borderRadius: '16px',
                        px: 3.5,
                        py: 1.3,
                        textTransform: 'none',
                        fontWeight: 800,
                        fontSize: '1rem',
                        background: 'linear-gradient(135deg,#2563eb,#7c3aed)',
                        boxShadow: '0 8px 24px rgba(124,58,237,0.35)'
                    }}
                >
                    Adicionar
                </Button>
            </Box>

            {/* TABS + BUSCA */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 3 }}>
                <Tabs
                    value={tab}
                    onChange={(e, v) => setTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{
                        minHeight: 48,
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 700,
                            minHeight: 48,
                            borderRadius: '14px',
                            mr: 1
                        },
                        '& .Mui-selected': {
                            bgcolor: isDark ? 'rgba(124,58,237,0.2)' : 'rgba(124,58,237,0.1)'
                        },
                        '& .MuiTabs-indicator': { display: 'none' }
                    }}
                >
                    <Tab value="produto" icon={<IconBox size={18} />} iconPosition="start" label="Produtos" />
                    <Tab value="servico" icon={<IconBriefcase size={18} />} iconPosition="start" label="Serviços" />
                </Tabs>

                <TextField
                    placeholder="Buscar por nome..."
                    size="small"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    sx={{ ml: { md: 'auto' }, minWidth: 260, '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
                    InputProps={{
                        startAdornment: <IconSearch size={18} style={{ marginRight: 8, opacity: 0.6 }} />,
                        endAdornment: searchInput && (
                            <IconButton size="small" onClick={() => setSearchInput('')}>
                                <IconX size={16} />
                            </IconButton>
                        )
                    }}
                />
            </Box>

            {/* GRID */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : items.length === 0 ? (
                <Stack alignItems="center" spacing={1.5} sx={{ py: 8, opacity: 0.5 }}>
                    <IconPhotoOff size={40} />
                    <Typography variant="h5" fontWeight={700}>
                        {search ? 'Nenhum resultado pra essa busca.' : 'Nada por aqui ainda.'}
                    </Typography>
                </Stack>
            ) : (
                <>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                        {total} {total === 1 ? 'item' : 'itens'}
                    </Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                            gap: 2.5
                        }}
                    >
                        {items.map((it) => (
                            <Box
                                key={it.id}
                                onClick={() => history.push(`/dashboard/config/catalog_edit/${it.id}`)}
                                sx={{
                                    position: 'relative',
                                    borderRadius: '22px',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    aspectRatio: '1 / 1',
                                    bgcolor: cardBg,
                                    border: `1px solid ${cardBorder}`,
                                    backdropFilter: 'blur(6px)',
                                    transition: 'transform .18s ease, box-shadow .18s ease',
                                    '&:hover': {
                                        transform: 'translateY(-4px) scale(1.02)',
                                        boxShadow: isDark ? '0 16px 32px rgba(0,0,0,0.5)' : '0 16px 32px rgba(15,23,42,0.15)'
                                    },
                                    '&:hover .item-delete-btn': { opacity: 1 }
                                }}
                            >
                                {it.imagem_capa ? (
                                    <Box
                                        component="img"
                                        src={mediaUrl(appId, it.imagem_capa)}
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            filter: it.ativo ? 'none' : 'grayscale(1) brightness(0.7)',
                                            transition: 'filter .2s ease'
                                        }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: isDark
                                                ? 'linear-gradient(135deg, rgba(37,99,235,0.25), rgba(124,58,237,0.25))'
                                                : 'linear-gradient(135deg, #e0e7ff, #ede9fe)',
                                            filter: it.ativo ? 'none' : 'grayscale(1) brightness(0.85)'
                                        }}
                                    >
                                        {it.tipo === 'produto' ? (
                                            <IconBox size={48} strokeWidth={1.3} color={isDark ? '#c7d2fe' : '#4338ca'} />
                                        ) : (
                                            <IconBriefcase size={48} strokeWidth={1.3} color={isDark ? '#c7d2fe' : '#4338ca'} />
                                        )}
                                    </Box>
                                )}

                                {!it.ativo && (
                                    <Chip
                                        label="Inativo"
                                        size="small"
                                        sx={{
                                            position: 'absolute',
                                            top: 10,
                                            right: 10,
                                            fontWeight: 700,
                                            bgcolor: 'rgba(0,0,0,0.6)',
                                            color: '#fff'
                                        }}
                                    />
                                )}

                                <IconButton
                                    className="item-delete-btn"
                                    size="small"
                                    onClick={(e) => handleDelete(e, it)}
                                    disabled={deletingId === it.id}
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        left: 8,
                                        opacity: 0,
                                        transition: 'opacity .15s ease',
                                        bgcolor: 'rgba(0,0,0,0.55)',
                                        color: '#fff',
                                        '&:hover': { bgcolor: 'rgba(220,38,38,0.85)' }
                                    }}
                                >
                                    {deletingId === it.id ? <CircularProgress size={16} color="inherit" /> : <IconTrash size={16} />}
                                </IconButton>

                                <Box
                                    sx={{
                                        position: 'absolute',
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        p: 1.5,
                                        background: 'linear-gradient(0deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0) 100%)'
                                    }}
                                >
                                    <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.92rem', lineHeight: 1.2 }} noWrap>
                                        {it.nome}
                                    </Typography>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.78rem', fontWeight: 600 }} noWrap>
                                        {precoResumo(it)}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    {items.length < total && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <Button
                                onClick={handleLoadMore}
                                disabled={loadingMore}
                                startIcon={loadingMore ? <CircularProgress size={16} /> : null}
                                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '14px' }}
                                variant="outlined"
                            >
                                Carregar mais ({total - items.length} restantes)
                            </Button>
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default ItemsHubPage;
